import { useApolloClient } from "@apollo/client";
import { useRef, useState } from "react";
import DatePicker from "react-date-picker";

import IconCheck from "../../images/icon_check.svg";
import LoadingIcon from "../../images/icon_loading_green.gif";
import {
  INVOLVED_AFFECTED_AREAS,
  LOAD_TYPE,
} from "../../utils/complaintsConstants";
import CheckableInput from "../CheckableInput/CheckableInput";
import Input from "../Input/Input";
import MultiSelectStyled from "../MultiSelectStyled/MultiSelectStyled";
import {
  AuxContainer,
  ClientSection,
  Container,
  GreenButton,
  RadioContainer,
  RowBy2,
  RowBy4,
  SectionTitle,
  Subtitle,
} from "./AdminComplaintsProcessInfoStyles";
import { requestDataProcess } from "./requestData";
import {
  ClientInfo,
  FormattedProcessInformation,
} from "../AdminComplaints/types";
import { Toast } from "primereact/toast";

function AdminComplaintsProcessInfo(props: {
  processInfo: FormattedProcessInformation;
  onProcessInfoChange: (updateInfo: any) => void;
  complaintId: string;
  refreshPage: () => void;
  clientHook: [
    ClientInfo[],
    React.Dispatch<React.SetStateAction<ClientInfo[]>>
  ];
}) {
  const client = useApolloClient();
  const toast = useRef<Toast>();
  const {
    processInfo,
    onProcessInfoChange,
    complaintId,
    refreshPage,
    clientHook,
  } = props;
  const [waitingForServerResponse, updateWaitingForServerResponse] =
    useState(false);
  const [clients, updateClients] = clientHook;
  const onClick = () => {
    if (waitingForServerResponse) return;
    const formattedClients = clients.filter((client) => client.inputValue);
    updateWaitingForServerResponse(true);
    requestDataProcess(client, formattedClients, complaintId, processInfo)
      .then(() => {
        refreshPage();
      })
      .catch((err) => {
        toast.current.show({
          severity: "error",
          summary: "Hubo un fallo al actualizar la información",
          contentClassName: "",
        });
      })
      .finally(() => updateWaitingForServerResponse(false));
  };

  return (
    <Container>
      <Toast ref={toast}></Toast>
      <SectionTitle>Clientes</SectionTitle>
      <ClientSection>
        {clients.map((client, index) => {
          return (
            <div key={index}>
              <Subtitle>{client.label}</Subtitle>
              <CheckableInput
                height="5rem"
                width="25.6rem"
                checked={client.checked}
                onChangeCheck={() => {
                  const newClients = [...clients];
                  newClients[index].checked = !clients[index].checked;
                  newClients[index].inputValue = "";
                  updateClients(newClients);
                }}
                value={client.inputValue}
                placeholder={client.label}
                onChangeValue={(ev) => {
                  const newClients = [...clients];
                  newClients[index].inputValue = ev.target.value;
                  updateClients(newClients);
                }}
              />
            </div>
          );
        })}
      </ClientSection>
      <div>
        <SectionTitle>Procesos</SectionTitle>
        <RowBy2>
          <div>
            <Subtitle>Áreas involucradas:</Subtitle>
            <MultiSelectStyled
              height="5rem"
              width="52.8rem"
              selectItems={INVOLVED_AFFECTED_AREAS}
              info={processInfo.involvedAreas}
              onInfoChange={(ev) => {
                onProcessInfoChange({ involvedAreas: ev.target.value });
              }}
            />
          </div>
          <div>
            <Subtitle>Áreas afectadas:</Subtitle>
            <MultiSelectStyled
              height="5rem"
              width="52.8rem"
              selectItems={INVOLVED_AFFECTED_AREAS}
              info={processInfo.affectedAreas}
              onInfoChange={(ev) => {
                onProcessInfoChange({ affectedAreas: ev.target.value });
              }}
            />
          </div>
        </RowBy2>
      </div>
      <div>
        <SectionTitle>Carga</SectionTitle>
        <RowBy2>
          <div>
            <Subtitle>Tipo de carga:</Subtitle>
            <MultiSelectStyled
              height="5rem"
              width="52.8rem"
              selectItems={LOAD_TYPE}
              info={processInfo.loadType}
              onInfoChange={(ev) => {
                onProcessInfoChange({ loadType: ev.target.value });
              }}
            />
          </div>
          <div>
            <Subtitle>Contenido de la carga:</Subtitle>
            <Input
              height="5rem"
              width="53.2rem"
              value={processInfo.label}
              onChange={(ev) => onProcessInfoChange({ label: ev.target.value })}
              placeholder="Contenido de la carga"
            />
          </div>
        </RowBy2>
      </div>
      <div>
        <SectionTitle>Comercial</SectionTitle>
        <div>
          <Subtitle>Ejecutivo comercial (cliente final):</Subtitle>
          <Input
            height="5rem"
            width="34.8rem"
            value={processInfo.executiveName}
            onChange={(ev) =>
              onProcessInfoChange({ executiveName: ev.target.value })
            }
            placeholder="Ejecutivo comercial"
          />
        </div>
      </div>
      <div>
        <SectionTitle>Seguridad</SectionTitle>
        <RowBy4>
          <div>
            <Subtitle>Solicitar videovigilancia:</Subtitle>
            <RadioContainer>
              <label>
                <input
                  type="radio"
                  checked={processInfo.requestVideo}
                  onChange={() =>
                    onProcessInfoChange({
                      requestVideo: !processInfo.requestVideo,
                    })
                  }
                />
                Sí
              </label>
              <label>
                <input
                  type="radio"
                  checked={!processInfo.requestVideo}
                  onChange={() =>
                    onProcessInfoChange({
                      requestVideo: !processInfo.requestVideo,
                      videoRequestDate: null,
                      videoUploadDate: null,
                      processNumber: "",
                    })
                  }
                />
                No
              </label>
            </RadioContainer>
          </div>
          {processInfo.requestVideo && (
            <div>
              <Subtitle>Fecha solicitud de video:</Subtitle>
              <DatePicker
                value={processInfo.videoRequestDate}
                onChange={(newDate) =>
                  onProcessInfoChange({ videoRequestDate: newDate })
                }
                clearIcon={null}
                dayPlaceholder={"dd"}
                locale={"es-PE"}
                monthPlaceholder={"mm"}
                showLeadingZeros={true}
                yearPlaceholder={"aaaa"}
              />
            </div>
          )}
          {processInfo.requestVideo && (
            <div>
              <Subtitle>Fecha recepción de video:</Subtitle>
              <DatePicker
                value={processInfo.videoUploadDate}
                onChange={(newDate) =>
                  onProcessInfoChange({ videoUploadDate: newDate })
                }
                clearIcon={null}
                dayPlaceholder={"dd"}
                locale={"es-PE"}
                monthPlaceholder={"mm"}
                showLeadingZeros={true}
                yearPlaceholder={"aaaa"}
              />
            </div>
          )}
          {processInfo.requestVideo && (
            <div>
              <Subtitle>Procesos a revisar:</Subtitle>
              <Input
                height="5rem"
                width="25.6rem"
                value={processInfo.processNumber}
                onChange={(ev) =>
                  onProcessInfoChange({ processNumber: ev.target.value })
                }
                placeholder="Nro. de procesos"
                type="number"
              />
            </div>
          )}
        </RowBy4>
        <RowBy4>
          <div>
            <Subtitle>Incidencia:</Subtitle>
            <RadioContainer>
              <label>
                <input
                  type="radio"
                  checked={processInfo.isIncidence}
                  onChange={() =>
                    onProcessInfoChange({
                      isIncidence: !processInfo.isIncidence,
                    })
                  }
                />
                Sí
              </label>
              <label>
                <input
                  type="radio"
                  checked={!processInfo.isIncidence}
                  onChange={() =>
                    onProcessInfoChange({
                      isIncidence: !processInfo.isIncidence,
                      incidenceDate: null,
                    })
                  }
                />
                No
              </label>
            </RadioContainer>
          </div>
          {processInfo.isIncidence && (
            <div>
              <Subtitle>Fecha de incidencia:</Subtitle>
              <DatePicker
                value={processInfo.incidenceDate}
                onChange={(newDate) =>
                  onProcessInfoChange({ incidenceDate: newDate })
                }
                clearIcon={null}
                dayPlaceholder={"dd"}
                locale={"es-PE"}
                monthPlaceholder={"mm"}
                showLeadingZeros={true}
                yearPlaceholder={"aaaa"}
              />
            </div>
          )}
        </RowBy4>
      </div>
      <div>
        <SectionTitle>Operaciones</SectionTitle>
        <RowBy4>
          <div>
            <Subtitle>Fecha solicitud trazabilidad:</Subtitle>
            <DatePicker
              value={processInfo.traceabilityRequestDate}
              onChange={(newDate) =>
                onProcessInfoChange({ traceabilityRequestDate: newDate })
              }
              clearIcon={null}
              dayPlaceholder={"dd"}
              locale={"es-PE"}
              monthPlaceholder={"mm"}
              showLeadingZeros={true}
              yearPlaceholder={"aaaa"}
            />
          </div>
          <div>
            <Subtitle>Fecha entrega trazabilidad:</Subtitle>
            <DatePicker
              value={processInfo.traceabilityDeliveryDate}
              onChange={(newDate) =>
                onProcessInfoChange({ traceabilityDeliveryDate: newDate })
              }
              clearIcon={null}
              dayPlaceholder={"dd"}
              locale={"es-PE"}
              monthPlaceholder={"mm"}
              showLeadingZeros={true}
              yearPlaceholder={"aaaa"}
            />
          </div>
          <div>
            <Subtitle>Fecha envío respuesta:</Subtitle>
            <DatePicker
              value={processInfo.responseDate}
              onChange={(newDate) =>
                onProcessInfoChange({ responseDate: newDate })
              }
              clearIcon={null}
              dayPlaceholder={"dd"}
              locale={"es-PE"}
              monthPlaceholder={"mm"}
              showLeadingZeros={true}
              yearPlaceholder={"aaaa"}
            />
          </div>
        </RowBy4>
      </div>
      <AuxContainer>
        <GreenButton onClick={onClick}>
          {!waitingForServerResponse ? (
            <img src={IconCheck} alt="check" />
          ) : (
            <img src={LoadingIcon} className="icon" alt="spinner" />
          )}
          <h2>Guardar datos del proceso</h2>
        </GreenButton>
      </AuxContainer>
    </Container>
  );
}

export default AdminComplaintsProcessInfo;
